<template>
  <div>
    <div class="air__utils__heading">
      <h5>Google Workspace Integration</h5>
    </div>

    <div class="row">
      <div class="col-md-6">
        <a-card title="Connection Status">
          <a-spin :spinning="loading">
            <div v-if="status && status.is_connected">
              <a-alert type="success" message="Connected" :description="'Successfully connected to ' + status.google_domain" showIcon class="mb-4" />

              <p><strong>Admin Email:</strong> {{ status.google_admin_email }}</p>
              <p><strong>Connected At:</strong> {{ status.connected_at }}</p>

              <div class="mt-4">
                <a-form layout="vertical">
                  <a-form-item label="Subscription Tier">
                    <a-select v-model="selectedTier" style="width: 100%" @change="updateTier" :loading="tierLoading">
                      <a-select-option value="fundamentals">Google Workspace for Education Fundamentals (Free)</a-select-option>
                      <a-select-option value="teaching_learning">Teaching and Learning Upgrade</a-select-option>
                      <a-select-option value="education_plus">Education Plus</a-select-option>
                    </a-select>
                  </a-form-item>
                </a-form>
              </div>

              <div class="mt-4 pt-3 border-top">
                <a-popconfirm
                  title="Are you sure you want to disconnect Google Workspace?"
                  @confirm="disconnect"
                  okText="Yes, disconnect"
                  cancelText="Cancel"
                >
                  <a-button type="danger" :loading="actionLoading">Disconnect Account</a-button>
                </a-popconfirm>
              </div>
            </div>

            <div v-else>
              <a-alert type="warning" message="Not Connected" description="Connect your school's Google Workspace for Education account to enable Google Meet, Google Classroom, and Google Drive integrations." showIcon class="mb-4" />

              <a-button type="primary" size="large" icon="google" @click="connect" :loading="actionLoading">
                Connect Google Workspace
              </a-button>
            </div>
          </a-spin>
        </a-card>
      </div>

      <div class="col-md-6">
        <a-card title="Features & Permissions">
          <p>By connecting Google Workspace, you enable:</p>
          <ul>
            <li><strong>Google Meet:</strong> Automatically generate Meet links for subjects and sync them to the student timetable.</li>
            <li><strong>Google Classroom:</strong> Link existing Google Classroom courses to subjects so students can view coursework.</li>
            <li><strong>Class Recordings:</strong> Allow teachers to upload or link Google Drive videos for students to watch securely.</li>
          </ul>

          <a-alert type="info" class="mt-3">
            <div slot="message">
              Note: The "Fundamentals" tier is free but does not include Google Meet recording. To allow teachers to record classes directly to Drive, you need the "Teaching and Learning Upgrade" or "Education Plus".
            </div>
          </a-alert>
        </a-card>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: true,
      actionLoading: false,
      tierLoading: false,
      status: null,
      selectedTier: 'fundamentals',
    }
  },
  mounted() {
    // Check if returning from OAuth callback
    if (this.$route.query.code && this.$route.query.state) {
      this.handleCallback(this.$route.query.code, this.$route.query.state)
    } else {
      this.fetchStatus()
    }
  },
  methods: {
    fetchStatus() {
      this.loading = true
      this.$http.get('/api/google-workspace/status')
        .then(({ data }) => {
          this.status = data.data
          if (this.status && this.status.subscription_tier) {
            this.selectedTier = this.status.subscription_tier
          }
        })
        .catch(err => {
          console.error(err)
          this.$notification.error({
            message: 'Error',
            description: 'Failed to fetch connection status.',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    connect() {
      this.actionLoading = true
      this.$http.get('/api/google-workspace/auth-url')
        .then(({ data }) => {
          if (data.data && data.data.auth_url) {
            window.location.href = data.data.auth_url
          }
        })
        .catch(err => {
          console.error(err)
          this.$notification.error({
            message: 'Error',
            description: 'Failed to initiate connection.',
          })
          this.actionLoading = false
        })
    },
    handleCallback(code, state) {
      this.loading = true
      this.$http.get('/api/google-workspace/callback', { params: { code, state } })
        .then(({ data }) => {
          this.$notification.success({
            message: 'Success',
            description: data.message,
          })
          // Clean up URL parameters
          this.$router.replace({ query: null })
          this.fetchStatus()
        })
        .catch(err => {
          console.error(err)
          let errorMsg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Failed to complete connection.'
          this.$notification.error({
            message: 'Connection Failed',
            description: errorMsg,
          })
          this.$router.replace({ query: null })
          this.fetchStatus()
        })
    },
    disconnect() {
      this.actionLoading = true
      this.$http.post('/api/google-workspace/disconnect')
        .then(({ data }) => {
          this.$notification.success({
            message: 'Disconnected',
            description: data.message,
          })
          this.status = { is_connected: false }
        })
        .catch(err => {
          console.error(err)
          this.$notification.error({
            message: 'Error',
            description: 'Failed to disconnect.',
          })
        })
        .finally(() => {
          this.actionLoading = false
        })
    },
    updateTier(value) {
      this.tierLoading = true
      this.$http.put('/api/google-workspace/tier', { subscription_tier: value })
        .then(({ data }) => {
          this.$notification.success({
            message: 'Success',
            description: 'Subscription tier updated successfully.',
          })
        })
        .catch(err => {
          console.error(err)
          this.$notification.error({
            message: 'Error',
            description: 'Failed to update subscription tier.',
          })
          this.selectedTier = this.status.subscription_tier // Revert to previous
        })
        .finally(() => {
          this.tierLoading = false
        })
    },
  },
}
</script>
